/* ============================================================================
   haiku-preview — the expanded preview keeps the photograph you were looking
   at, and pages through the rest.

   TWO PROBLEMS THIS SOLVES
   1. haiku-gallery.js line 394 sets previewImg.src from the slide's FIRST
      .gallery__img — always the primary. So hovering a tile to reveal the
      second photograph and then clicking it threw that photograph away.
   2. The preview only ever showed one image. Products have more.

   ADDITIVE AND FAIL-SAFE. This never edits haiku-gallery.js (whose asset hash
   is pinned by cached HTML and slow to land). It observes the preview opening,
   then sets the src and adds controls. If it throws, is blocked, or the slide
   carries no data-media, the preview is exactly what it is today: the gallery's
   own behaviour, untouched.

   ⚠️ It deliberately does NOT touch the image during the Flip transition.
   haiku-gallery.js animates previewImg (scale 1.2 -> 1 over ~1.2s); swapping
   src mid-tween shows a half-scaled wrong frame. The swap waits for the
   transition to settle.
   ========================================================================== */
(function () {
  'use strict';

  var cfg = window.haikuPreviewConfig || {};
  if (cfg.enabled === false) return;

  /* Requires a `.gallery__slide` with a media list — which the collection
     emits for real, and which haiku-lightbox emits (hidden) on product pages
     precisely so this layer runs there too. Blocking it was the earlier,
     wrong answer: it left the product gallery without the arrows, the paging
     transitions and the scrim that live in THIS file, and no re-derivation of
     them was ever going to match. */
  if (!document.querySelector('.gallery__slide[data-media]')) return;

  /* Desktop only. Mobile slides navigate straight to the product page, so
     every one of this file's jobs — prefetching preview media, placing the
     panel nav, wiring the scrim — is work for a panel that never opens. */
  if (window.matchMedia && window.matchMedia('(max-width: 900px)').matches) return;

  var SETTLE = typeof cfg.settle === 'number' ? cfg.settle : 900;
  var FADE = typeof cfg.fade === 'number' ? cfg.fade : 260;

  var state = { slide: null, index: 0, media: [], img: null, wired: false };

  function mediaOf(slide) {
    var raw = slide && slide.getAttribute('data-media');
    if (!raw) return [];
    return raw.split('|').filter(Boolean);
  }

  /* ── remember which frame the pointer revealed ─────────────────────────── */

  function trackHover() {
    var slides = document.querySelectorAll('.gallery__slide');
    for (var i = 0; i < slides.length; i++) {
      (function (slide) {
        slide.addEventListener('pointerenter', function () {
          slide.__hpIndex = 1;
          /* Warm the preview's photographs on hover.
             The first open flickered because the preview asks for a
             1400px-wide render the grid never requested — the tile is served
             at tile size. So on the first click that URL is cold, and the
             Flip runs against an image that has not decoded yet. Every later
             open is cached, which is exactly the shape of the bug reported.
             Hovering precedes clicking, so this is free lead time.
             decoding='async' keeps the fetch off the main thread. */
          if (slide.__hpWarm) return;
          slide.__hpWarm = true;
          var m = mediaOf(slide);
          for (var w = 0; w < m.length && w < 3; w++) {
            var im = new Image();
            im.decoding = 'async';
            im.src = m[w];
          }
        });
        slide.addEventListener('pointerleave', function () { slide.__hpIndex = 0; });
        // Capture phase: the gallery calls preventDefault on click, and we need
        // the slide identity before it opens, not after.
        slide.addEventListener('click', function () {
          var m = mediaOf(slide);
          state.slide = slide;
          state.media = m;
          // Only carry the hover frame across if that photograph exists.
          state.index = (slide.__hpIndex === 1 && m.length > 1) ? 1 : 0;
          if (state.index > 0) armSrcIntercept();
        }, true);
      })(slides[i]);
    }
  }

  /* ── carry the hovered frame with NO flash of the primary ───────────────
     haiku-gallery.js writes previewImg.src from the slide's first
     .gallery__img the moment the preview opens. Correcting that afterwards on
     a timer meant the primary photograph was on screen for the whole settle
     window — ~900ms of looking at the wrong picture before it changed. That is
     the "it switches to the first photo" everyone sees.

     A MutationObserver on the src attribute fires in a microtask, before the
     browser paints, so rewriting it there is invisible: the gallery's value
     never reaches a frame. One-shot — it disarms itself so paging afterwards
     is untouched. */
  function armSrcIntercept() {
    var img = state.img;
    if (!img || !window.MutationObserver) return;
    var want = state.media[state.index];
    if (!want) return;

    var mo = new MutationObserver(function () {
      // Compare on the resolved URL; the attribute may be relative.
      if (img.src && img.src.indexOf(want) === -1) {
        img.src = want;
      }
      mo.disconnect();
    });
    mo.observe(img, { attributes: true, attributeFilter: ['src'] });
    // Never leave an observer armed if the preview does not open.
    window.setTimeout(function () { mo.disconnect(); }, 4000);
  }

  /* ── the controls ──────────────────────────────────────────────────────── */

  /* Demo 2's mechanism: the photograph itself pages. Two invisible halves over
     the image, `cursor: none`, with the CURSOR carrying the arrow — crossing
     the midline turns the arrow rather than swapping an icon. They are created
     here rather than in Liquid because they must know the media count: one
     photograph has nothing to page to, and a dead click target is worse than
     no target. */
  function ensureHalves(content) {
    if (content.querySelector('.hp-half')) return;

    /* Both halves live in ONE wrapper, and that wrapper is what the cursor
       treats as the navigation region.

       Why it matters: the cursor picks between two very different animations
       on `navRegion !== nav` —
         navEnter()  -> spinTo(..., instant = TRUE)   the fly-in
         navSwitch() -> spinTo(..., instant = false)  the animated flip
       With each half its own element, crossing the midline changed the region
       every time, so it always took the INSTANT path and the arrow snapped.
       Demo 2 treats the whole photograph as a single region and lets the side
       change within it, which is what produces the turn. */
    var wrap = document.createElement('div');
    wrap.className = 'hp-halves';

    ['prev', 'next'].forEach(function (dir) {
      var b = document.createElement('button');
      b.type = 'button';
      b.className = 'hp-half hp-half--' + dir;
      b.setAttribute('data-hp-half', dir);
      b.setAttribute('aria-label', dir === 'prev' ? 'Previous photograph' : 'Next photograph');
      b.addEventListener('click', function (e) {
        e.stopPropagation();
        step(dir === 'prev' ? -1 : 1);
      });
      wrap.appendChild(b);
    });

    content.appendChild(wrap);
  }

  /* The X measured x=742 in a 1440 viewport with `position: fixed; right:
     2.6rem` — the offsets were simply ignored. Cause: GSAP Flip animates the
     preview with transforms, and a transformed ancestor becomes the containing
     block for position:fixed descendants. The X was resolving against the
     animated box, not the viewport. (Identical to what trapped the search
     CLOSE inside .sidebar__container.)

     No CSS fixes this from inside the subtree, so the button is moved OUT of
     it — reparented to <body>, where nothing is transformed. Moving a node
     preserves its listeners, so the close behaviour comes with it. */
  /* The blurred page behind the panel.

     Appended to <body>, not inside .content — .content is inset 40px AND is
     transformed by the Flip tween, and a transformed ancestor becomes the
     containing block for position:fixed descendants. A scrim built inside it
     could never cover the viewport. Same trap that caught the close button.

     Removed on close so the blur never outlives the panel. */
  function ensureScrim() {
    var el = document.querySelector('.hp-scrim');
    if (el) return el;
    el = document.createElement('div');
    el.className = 'hp-scrim';
    el.setAttribute('aria-hidden', 'true');
    document.body.appendChild(el);
    // Commit the start state before the class that transitions it.
    void el.getBoundingClientRect();
    el.setAttribute('data-hp-in', '1');
    return el;
  }

  function dropScrim() {
    var el = document.querySelector('.hp-scrim');
    if (!el) return;
    el.removeAttribute('data-hp-in');
    window.setTimeout(function () {
      if (el.parentNode) el.parentNode.removeChild(el);
    }, 320);
  }

  /* The scrim outlived a close in testing. Rather than trust one call site,
     the open/closed class is the single source of truth: if hp-open is gone
     and a scrim is still there, it goes. A blurred page with no panel over it
     is a dead-end for the visitor, so this is worth the belt and braces. */
  function reapScrim() {
    if (document.documentElement.classList.contains('hp-open')) return;
    var el = document.querySelector('.hp-scrim');
    if (el && el.parentNode) el.parentNode.removeChild(el);
  }

  /* ⚠️ THE 500ms SWEEP IS GONE. haiku-preview.css:578 declares
     `.hp-scrim { display: none !important }` unconditionally, superseding the
     block at :399-411, so this timer spent the life of every gallery and
     product page maintaining an element that can never be seen.
     reapScrim/ensureScrim/dropScrim are left in place — restoring the scrim
     means deleting that one CSS rule and putting this line back. */
  // window.setInterval(reapScrim, 500);
  window.addEventListener('pageshow', reapScrim);

  function liftClose() {
    var btn = document.querySelector('.content__back');
    if (!btn || btn.parentNode === document.body) return;
    btn.setAttribute('data-hp-lifted', '1');
    document.body.appendChild(btn);
  }

  /* THE PAGER'S PLACE IN THE DOM, not its position in CSS.

     On a phone the panel stacks — photograph, then copy — and the pager was
     rendered last, so it sat at the foot of the panel, far from the photograph
     it controls. Every CSS attempt to move it failed for a different reason
     (absolute positioning inherited a `bottom` and stretched the control to
     494px; a transform pushed the copy off-screen; flex ordering required
     setting `display` on .content, which is the property the open/close logic
     reads and broke the panel entirely).

     Moving the NODE is the honest fix: no positioning, nothing for another
     rule to fight, and the desktop order is restored above the breakpoint so
     one layout cannot strand the other. */
  function placeNav(content) {
    var nav = content.querySelector('.hp-nav');
    var photo = content.querySelector('.content__preview-img');
    if (!nav || !photo || !photo.parentNode) return;

    var mobile = !!(window.matchMedia && window.matchMedia('(max-width: 900px)').matches);

    if (mobile) {
      // Straight after the photograph.
      if (photo.nextSibling !== nav) {
        photo.parentNode.insertBefore(nav, photo.nextSibling);
        nav.setAttribute('data-hp-nav-moved', '1');
      }
    } else if (nav.getAttribute('data-hp-nav-moved')) {
      // Back to last, where the desktop layout expects it.
      content.appendChild(nav);
      nav.removeAttribute('data-hp-nav-moved');
    }
  }

  function ensureControls(content) {
    ensureHalves(content);
    if (content.querySelector('.hp-nav')) return;

    var nav = document.createElement('div');
    nav.className = 'hp-nav';
    nav.innerHTML =
      '<button type="button" class="hp-nav__btn hp-nav__btn--prev" aria-label="Previous photograph">' +
        '<svg viewBox="0 0 22 22" width="22" height="22" fill="none" aria-hidden="true">' +
        '<path d="M13.5 4.5L7 11l6.5 6.5" stroke="currentColor" stroke-width="1.75" stroke-linecap="round" stroke-linejoin="round"/></svg>' +
      '</button>' +
      '<span class="hp-nav__count" aria-live="polite"></span>' +
      '<button type="button" class="hp-nav__btn hp-nav__btn--next" aria-label="Next photograph">' +
        '<svg viewBox="0 0 22 22" width="22" height="22" fill="none" aria-hidden="true">' +
        '<path d="M8.5 4.5L15 11l-6.5 6.5" stroke="currentColor" stroke-width="1.75" stroke-linecap="round" stroke-linejoin="round"/></svg>' +
      '</button>';
    content.appendChild(nav);

    nav.querySelector('.hp-nav__btn--prev').addEventListener('click', function (e) {
      e.stopPropagation(); step(-1);
    });
    nav.querySelector('.hp-nav__btn--next').addEventListener('click', function (e) {
      e.stopPropagation(); step(1);
    });

    document.addEventListener('keydown', function (e) {
      if (!document.documentElement.classList.contains('hp-open')) return;
      if (e.key === 'ArrowLeft') { e.preventDefault(); step(-1); }
      if (e.key === 'ArrowRight') { e.preventDefault(); step(1); }
    });
  }

  function crossfade(src) {
    var img = state.img;
    if (!img || !src) return;
    img.style.transition = 'opacity ' + FADE + 'ms ease';
    img.style.opacity = '0';
    window.setTimeout(function () {
      img.src = src;
      // Only fade back in once the new frame has actually decoded, or the
      // fade reveals an empty box.
      var show = function () { img.style.opacity = '1'; };
      if (img.decode) img.decode().then(show).catch(show);
      else show();
    }, FADE);
  }

  /* The shader layer, if it is loaded, gets first refusal — and is handed the
     crossfade above to call if it cannot run. snippets/haiku-preview-fx.liquid
     defines it; delete that render line and this is the crossfade again, with
     no other change anywhere. */
  function swap(src, dir) {
    var img = state.img;
    if (!img || !src) return;
    var fx = window.haikuPreviewFx;
    if (fx && typeof fx.transition === 'function') {
      fx.transition(img, src, dir || 1, function () { crossfade(src); });
      return;
    }
    crossfade(src);
  }

  function step(dir) {
    if (state.media.length < 2) return;
    state.index = (state.index + dir + state.media.length) % state.media.length;
    swap(state.media[state.index], dir);
    paint();
  }

  function paint() {
    var nav = document.querySelector('.hp-nav');
    /* ⚠️ `.hg .content`, NOT `.content`. The vendor theme uses that class for a
       cart line's text block too (snippets/cart-form.liquid:39), and it comes
       FIRST in the DOM — so on any page with something in the cart this
       matched the cart item and wrote `display: none` onto it inline.
       Measured on /cart: the product title 0x0 inside a `.content` that this
       file had hidden, while the photograph beside it rendered normally. */
    var content = document.querySelector('.hg .content');
    var many = state.media.length > 1;
    // Drives the CSS that hides both the halves and the arrows.
    if (content) content.setAttribute('data-hp-single', many ? '0' : '1');
    if (!nav) return;
    nav.style.display = many ? '' : 'none';
    var count = nav.querySelector('.hp-nav__count');
    if (count) count.textContent = many ? (state.index + 1) + ' / ' + state.media.length : '';
  }

  /* ── watch the preview open and close ──────────────────────────────────── */

  function boot() {
    /* ⚠️ `.hg .content`, NOT `.content`. The vendor theme uses that class for a
       cart line's text block too (snippets/cart-form.liquid:39), and it comes
       FIRST in the DOM — so on any page with something in the cart this
       matched the cart item and wrote `display: none` onto it inline.
       Measured on /cart: the product title 0x0 inside a `.content` that this
       file had hidden, while the photograph beside it rendered normally. */
    var content = document.querySelector('.hg .content');
    var preview = document.querySelector('.content__preview-img');
    if (!content || !preview) return;

    state.img = preview.tagName === 'IMG' ? preview : preview.querySelector('img');
    if (!state.img) return;

    trackHover();
    ensureControls(content);

    var isOpen = function () {
      return getComputedStyle(content).display !== 'none';
    };

    var open = false;
    var check = function () {
      var now = isOpen();
      if (now === open) return;
      open = now;
      document.documentElement.classList.toggle('hp-open', now);

      /* Closing: leave the tile showing the photograph the visitor ended on.
         Paging to the 4th image and closing used to snap back to the 1st,
         which reads as the site forgetting where you were. The slide's own
         <img> is repointed so the grid keeps that frame.

         Only the primary img is touched — the hover-reveal alt image is left
         alone, so the mask wipe still has its second photograph to show. */
      if (!now) {
        dropScrim();
        var sl = state.slide;
        var seen = state.media[state.index];
        if (sl && seen && state.index > 0) {
          var prim = sl.querySelector('.gallery__img:not(.gallery__img--alt)');
          if (prim && prim.src.indexOf(seen) === -1) {
            prim.removeAttribute('srcset');
            prim.src = seen;
          }
        }
        return;
      }

      // Wait for the Flip tween to finish before touching the image.
      window.setTimeout(function () {
        if (!isOpen()) return;
        liftClose();
        ensureScrim();
        placeNav(content);
        if (!state.media.length && state.slide) state.media = mediaOf(state.slide);
        paint();
        // Warm the shader layer's textures for this product, if it is loaded,
        // so the first click is not waiting on a fetch. No-op without it.
        var fx = window.haikuPreviewFx;
        if (fx && typeof fx.prefetch === 'function') fx.prefetch(state.media);
        // Fallback only. armSrcIntercept() normally lands the right photograph
        // before first paint; this catches the case where the observer missed
        // (no MutationObserver, or the gallery set src before the click
        // listener ran). Skip it when the image is already correct, so we do
        // not run a transition from a photograph to itself.
        var want = state.media[state.index];
        if (state.index > 0 && want && state.img && state.img.src.indexOf(want) === -1) {
          swap(want, 1);
        }
      }, SETTLE);
    };

    window.addEventListener('resize', function () { placeNav(content); });

    new MutationObserver(check).observe(content, { attributes: true, attributeFilter: ['style', 'class'] });
    check();
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();
})();
